import { z } from 'zod';

const id = z.string().min(1);
const timeText = z.string().nullable();

export const publicServiceSchema = z.object({
  id,
  code: z.string(),
  category: z.string(),
  name: z.string(),
  description: z.string(),
  priceMinor: z.number(),
  priceKind: z.enum(['fixed', 'from']),
  durationMinutes: z.number(),
  occupancy: z.enum(['within_hours', 'continuous']),
  isFeatured: z.boolean(),
});
export type PublicService = z.infer<typeof publicServiceSchema>;

export const publicTenantSchema = z.object({
  id,
  slug: z.string(),
  status: z.enum(['preview', 'live', 'archived']),
  isPreview: z.boolean(),
  name: z.string(),
  shortName: z.string(),
  tagline: z.string(),
  description: z.string(),
  address: z.string(),
  directions: z.string(),
  mapUrl: z.string().nullable(),
  phone: z.string(),
  timezone: z.string(),
  currency: z.string(),
  accentColor: z.string(),
  logoPath: z.string().nullable(),
  heroPath: z.string().nullable(),
  heroAlt: z.string(),
  aiEnabled: z.boolean(),
  resourceCount: z.number().int().nonnegative().default(0),
  rules: z.object({
    slotStepMinutes: z.number(),
    minLeadMinutes: z.number(),
    bookingHorizonDays: z.number(),
    cancelCutoffHours: z.number(),
    cancellationPolicy: z.string(),
  }),
  services: z.array(publicServiceSchema),
  hours: z.array(z.object({ weekday: z.number(), opensAt: z.string(), closesAt: z.string() })),
  exceptions: z.array(z.object({ day: z.string(), opensAt: timeText, closesAt: timeText, note: z.string() })),
  infoCards: z.array(z.object({ position: z.number(), icon: z.string(), title: z.string(), body: z.string() })),
  gallery: z.array(z.object({ id, imagePath: z.string(), caption: z.string(), alt: z.string() })),
  today: z.string(),
});
export type PublicTenant = z.infer<typeof publicTenantSchema>;

export const availabilitySchema = z.object({
  serviceId: id,
  timezone: z.string(),
  from: z.string(),
  to: z.string(),
  lastBookableDay: z.string(),
  days: z.array(
    z.object({
      date: z.string(),
      slots: z.array(z.object({ startsAt: z.string(), time: z.string() })),
    }),
  ),
});
export type Availability = z.infer<typeof availabilitySchema>;

export const bookingStatusSchema = z.enum(['confirmed', 'arrived', 'completed', 'cancelled', 'no_show']);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const reminderSchema = z.object({
  status: z.string(),
  runAt: z.string().optional(),
  skipReason: z.string().nullable().optional(),
});

export const publicBookingSchema = z.object({
  id,
  code: z.string(),
  status: bookingStatusSchema,
  startsAt: z.string(),
  endsAt: z.string(),
  service: z.object({
    id,
    name: z.string(),
    priceMinor: z.number(),
    priceKind: z.enum(['fixed', 'from']),
    currency: z.string(),
    durationMinutes: z.number(),
  }),
  resourceName: z.string(),
  customer: z.object({ name: z.string(), phone: z.string() }).optional(),
  car: z.object({ model: z.string(), plate: z.string().nullable() }),
  comment: z.string().nullable(),
  isDemo: z.boolean(),
  createdAt: z.string(),
  cancelledAt: z.string().nullable(),
  cancelledBy: z.string().nullable(),
  cancelDeadline: z.string(),
  canCancel: z.boolean(),
  pushEnabled: z.boolean(),
  reminder: reminderSchema,
  studio: z.object({
    slug: z.string(),
    name: z.string(),
    address: z.string(),
    phone: z.string(),
    timezone: z.string(),
    mapUrl: z.string().nullable(),
    cancellationPolicy: z.string(),
    cancelCutoffHours: z.number(),
  }),
  replayed: z.boolean().optional(),
  token: z.string().optional(),
});
export type PublicBooking = z.infer<typeof publicBookingSchema>;
